import { create } from 'zustand';
import type { EquipSlotId, ItemId, MonsterId, PlayerState, SkillId } from '../domain/state';
import { simulateDetailed, type GameEvent } from '../domain/offline/simulate';
import {
  OFFLINE_MODAL_MIN_MS,
  runOfflineCatchup,
  type OfflineSummary,
} from '../domain/offline/summary';
import {
  findQuickTarget,
  getSlot,
  type Containers,
  type SlotRef,
} from '../domain/economy/containers';
import { transfer, type TransferFailureReason } from '../domain/economy/transfer';
import {
  equipFrom,
  totalBonuses,
  unequip,
  type EquipFailureReason,
} from '../domain/economy/equipment';
import {
  emptyState,
  loadFromLocalStorage,
  saveToLocalStorage,
  exportSaveAsBlob,
  importSaveFromString,
  unsupportedFutureSaveVersion,
  UnsupportedFutureSaveError,
} from '../domain/save/io';
import { getMonster } from '../domain/monsters/spec';
import { PHASE_LABELS } from '../domain/monsters/boss';
import { canAcceptQuest, monsterAvailability } from '../domain/quests/eligibility';
import {
  applyHarrowChoice,
  type HarrowChoice,
  type HarrowResultKey,
} from '../domain/quests/dialogue';
import { getNpc, ITEMS, QUESTS, TOASTS } from '../data';

type Page =
  | 'skills'
  | 'combat'
  | 'bank'
  | 'inventory'
  | 'equipment'
  | 'parliament'
  | 'quests'
  | 'settings';

const TRANSFER_FAILURE_TEXT: Record<TransferFailureReason, string> = {
  'invalid-slot': 'That slot does not exist.',
  'same-slot': 'Item is already there.',
  'empty-source': 'Nothing to move from that slot.',
  'invalid-quantity': 'Invalid quantity for that move.',
  'target-full': 'That stack is already full.',
  'partial-into-occupied': 'Move the whole stack or pick an empty slot.',
};

const EQUIP_FAILURE_TEXT: Record<EquipFailureReason, string> = {
  'invalid-slot': 'That slot does not exist.',
  'empty-source': 'Nothing to equip there.',
  'not-equippable': 'That item cannot be equipped.',
  'no-space': 'No inventory space for the displaced item.',
};

function containersOf(state: PlayerState): Containers {
  return { bank: state.bank, inventory: state.inventory };
}

function playerMaxHp(state: PlayerState): number {
  return 100 + totalBonuses(state.equipment).hp;
}

const LOOT_LOST_TEXT = 'Bank and inventory are full — new drops are being lost!';

/** Toast copy for one-shot unlock flags surfaced by the simulation. */
const FLAG_TOAST_TEXT: Record<string, string> = {
  codex_quarry: 'Codex Quarry unlocked. The full roster may now be debugged on purpose.',
  hatch_mode: 'HATCH MODE armed.',
};

function toastForEvent(event: GameEvent): string | null {
  switch (event.type) {
    case 'quest-complete':
      return `Quest complete: ${event.questName}.`;
    case 'flag-added':
      return FLAG_TOAST_TEXT[event.flag] ?? null;
    case 'boss-phase':
      return PHASE_LABELS[event.phase];
    case 'starter-grant':
      return 'Herman: one Polished Ladle, banked. Equipment is manners with numbers.';
    default:
      return null;
  }
}

/**
 * Saves are serialized through this queue so an older in-flight write can
 * never finish after (and overwrite) a newer one on disk.
 */
let saveQueue: Promise<void> = Promise.resolve();

/** Single-flight guard: concurrent catch-up requests share one application. */
let catchupInFlight: Promise<void> | null = null;

type GameStore = {
  state: PlayerState;
  page: Page;
  ready: boolean;
  lastSavedAt: string;
  toasts: ReadonlyArray<{ id: number; text: string; tone: 'good' | 'warn' }>;
  /** Selected transfer source; the shared select-then-activate model. */
  selected: SlotRef | null;
  /** Pending offline report; non-null renders the blocking modal. */
  offlineSummary: OfflineSummary | null;
  /** Last Parakeet crit quote — the combat UI operator log. */
  bossQuote: string | null;

  bootstrap: () => Promise<void>;
  setPage: (p: Page) => void;
  setActiveSkill: (s: SkillId) => void;
  setActiveMonster: (m: MonsterId) => void;
  stopActive: () => void;
  tick: (dtMs: number) => void;
  saveNow: () => Promise<void>;
  exportSave: () => void;
  importSave: (raw: string) => boolean;
  applyOfflineCatchup: () => Promise<void>;
  dismissOfflineSummary: () => void;
  pushToast: (text: string, tone?: 'good' | 'warn') => void;
  setBankTab: (tab: number) => void;
  setVolume: (key: 'soundVolume' | 'musicVolume', value: number) => void;

  selectSlot: (ref: SlotRef | null) => void;
  /** Click/Enter on a slot: select source, deselect, or move to target. */
  activateSlot: (ref: SlotRef) => void;
  /** One pure engine behind every path (click, keyboard, drag). */
  transferSlot: (from: SlotRef, to: SlotRef, qty: number) => boolean;
  /** Move the selection to the best slot of the other container. */
  quickMove: (qty?: number) => void;
  equipFromSlot: (ref: SlotRef) => void;
  unequipSlot: (slot: EquipSlotId) => void;
  acceptQuest: (questId: string) => void;
  /** Harrow's branching conversation: records the choice, returns the result key. */
  speakToHarrow: (choice: HarrowChoice) => HarrowResultKey;
};

export const useGame = create<GameStore>((set, get) => ({
  state: emptyState(),
  page: 'skills',
  ready: false,
  lastSavedAt: '',
  toasts: [],
  selected: null,
  offlineSummary: null,
  bossQuote: null,

  bootstrap: async () => {
    const loaded = await loadFromLocalStorage();
    const initial = loaded ?? emptyState();
    set({ state: initial, ready: true, lastSavedAt: initial.savedAt });
    const futureVersion = unsupportedFutureSaveVersion();
    if (futureVersion !== null) {
      get().pushToast(
        `Save v${futureVersion} belongs to a newer Hermanity Idle build. It is preserved; saving is disabled here.`,
        'warn',
      );
    }
    queueMicrotask(() => void get().applyOfflineCatchup());
  },

  setPage: (p) => set({ page: p }),
  setActiveSkill: (s) => {
    const cur = get().state;
    set({
      state: {
        ...cur,
        active: { kind: 'train', skillId: s },
        actionProgressMs: 0,
        hp: { player: playerMaxHp(cur), monster: null },
      },
    });
    void get().saveNow();
  },
  setActiveMonster: (m) => {
    const cur = get().state;
    const availability = monsterAvailability(cur, m);
    if (!availability.available) {
      get().pushToast(availability.reason, 'warn');
      return;
    }
    set({
      state: {
        ...cur,
        active: { kind: 'combat', monsterId: m, zone: 'codex_quarry' },
        actionProgressMs: 0,
        combatRound: 0,
        hp: { player: playerMaxHp(cur), monster: getMonster(m).hp },
      },
    });
    get().pushToast(
      (TOASTS.toasts.engaged ?? 'Engaged: {monster}.').replace('{monster}', getMonster(m).name),
      'good',
    );
    void get().saveNow();
  },
  stopActive: () => {
    const cur = get().state;
    set({
      state: {
        ...cur,
        active: { kind: 'idle' },
        actionProgressMs: 0,
        combatRound: 0,
        hp: { player: playerMaxHp(cur), monster: null },
      },
    });
    void get().saveNow();
  },
  tick: (dtMs) => {
    const cur = get().state;
    const { state: next, lostItems, events } = simulateDetailed(cur, dtMs);
    if (lostItems.length > 0 && !get().toasts.some((t) => t.text === LOOT_LOST_TEXT)) {
      get().pushToast(LOOT_LOST_TEXT, 'warn');
    }
    for (const event of events) {
      const text = toastForEvent(event);
      if (text) get().pushToast(text, 'good');
    }
    const crits = events.filter((e) => e.type === 'boss-crit');
    if (crits.length > 0) {
      const last = crits[crits.length - 1]!;
      if (last.type === 'boss-crit') set({ bossQuote: last.quote });
    }
    if (next === cur) return;
    // The operator log lives only for the encounter.
    if (next.active.kind !== 'combat' && get().bossQuote !== null) set({ bossQuote: null });
    set({ state: next });
    // Quest completions mutate durable rewards; persist promptly so a
    // reload right after cannot lose the exactly-once transition. An
    // encounter ending (kill OR death → idle) is equally durable — loot and
    // restored HP must survive an immediate reload too.
    const encounterEnded = cur.active.kind === 'combat' && next.active.kind !== 'combat';
    if (events.length > 0 || encounterEnded) void get().saveNow();
  },
  saveNow: () => {
    const run = async () => {
      // Serialized execution reads the freshest state at write time.
      const cur = get().state;
      let saved: PlayerState;
      try {
        saved = await saveToLocalStorage(cur);
      } catch (error) {
        if (error instanceof UnsupportedFutureSaveError) {
          const text = `Save v${error.storedVersion} belongs to a newer build and was not overwritten.`;
          if (!get().toasts.some((toast) => toast.text === text)) get().pushToast(text, 'warn');
          return;
        }
        throw error;
      }
      // Merge only the stamp fields into whatever the state is NOW — the
      // stamped snapshot may be stale and must never overwrite newer
      // gameplay mutations that landed while the write was in flight.
      const latest = get().state;
      set({
        state: {
          ...latest,
          version: saved.version,
          savedAt: saved.savedAt,
          lastSimulatedAt: saved.lastSimulatedAt,
          checksum: saved.checksum,
        },
        lastSavedAt: saved.savedAt,
      });
    };
    const next = saveQueue.then(run, run);
    saveQueue = next.catch(() => undefined);
    return next;
  },
  exportSave: () => {
    const cur = get().state;
    const blob = exportSaveAsBlob(cur);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hermanity-idle-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    get().pushToast(TOASTS.toasts.saveExported ?? 'Save exported.', 'good');
  },
  importSave: (raw) => {
    const parsed = importSaveFromString(raw);
    if (!parsed) {
      get().pushToast('Import failed: invalid or unsupported save.', 'warn');
      return false;
    }
    set({ state: parsed, selected: null });
    get().pushToast(TOASTS.toasts.saveImported ?? 'Save imported.', 'good');
    // The import path migrates like the load path; catch up its offline
    // window (which persists), then persist regardless — sequentially, so
    // a stale save cannot overwrite the caught-up state.
    void (async () => {
      await get().applyOfflineCatchup();
      await get().saveNow();
    })();
    return true;
  },
  applyOfflineCatchup: () => {
    if (catchupInFlight) return catchupInFlight;
    const run = (async () => {
      const cur = get().state;
      const epoch = new Date(0).toISOString();
      const anchor =
        cur.lastSimulatedAt && cur.lastSimulatedAt !== epoch ? cur.lastSimulatedAt : cur.savedAt;
      if (!anchor || anchor === epoch) return;
      const anchorMs = Date.parse(anchor);
      if (Number.isNaN(anchorMs)) return;
      const now = Date.now();
      const elapsed = Math.max(0, now - anchorMs);

      // Elapsed complete intervals ALWAYS simulate; the 5-minute threshold
      // only decides whether the report modal is worth showing.
      const { state: next, summary } = runOfflineCatchup(
        { ...cur, lastSimulatedAt: anchor },
        now,
        Math.random,
      );
      set({
        state: next,
        ...(elapsed >= OFFLINE_MODAL_MIN_MS ? { offlineSummary: summary } : {}),
      });
      for (const event of summary.events) {
        const text = toastForEvent(event);
        if (text) get().pushToast(text, 'good');
      }
      // Short boss-fight resets never reach the modal; explain via toast.
      if (summary.bossFightReset && elapsed < OFFLINE_MODAL_MIN_MS) {
        get().pushToast(
          'The Parakeet only screams while the tab is open — the fight was stopped and reset. No damage, no loot. Retry is free.',
          'warn',
        );
      }
      // Persist immediately so a reload cannot re-apply the same window.
      await get().saveNow();
    })();
    catchupInFlight = run.finally(() => {
      catchupInFlight = null;
    });
    return catchupInFlight;
  },
  dismissOfflineSummary: () => set({ offlineSummary: null }),
  pushToast: (text, tone = 'good') => {
    const id = Date.now() + Math.random();
    set((s) => ({ toasts: [...s.toasts, { id, text, tone }] }));
    setTimeout(() => {
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, 3500);
  },
  setBankTab: (tab) => {
    const cur = get().state;
    set({ state: { ...cur, bankTab: Math.max(0, Math.min(3, tab)) } });
  },
  setVolume: (key, value) => {
    const cur = get().state;
    const clamped = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
    set({ state: { ...cur, settings: { ...cur.settings, [key]: clamped } } });
    void get().saveNow();
  },

  selectSlot: (ref) => set({ selected: ref }),
  activateSlot: (ref) => {
    const { selected, state } = get();
    if (!selected) {
      if (getSlot(containersOf(state), ref)) set({ selected: ref });
      return;
    }
    if (selected.container === ref.container && selected.index === ref.index) {
      set({ selected: null });
      return;
    }
    const source = getSlot(containersOf(state), selected);
    if (!source) {
      set({ selected: null });
      return;
    }
    if (get().transferSlot(selected, ref, source.qty)) set({ selected: null });
  },
  transferSlot: (from, to, qty) => {
    const cur = get().state;
    const result = transfer(containersOf(cur), from, to, qty);
    if (!result.ok) {
      get().pushToast(TRANSFER_FAILURE_TEXT[result.reason], 'warn');
      return false;
    }
    set({ state: { ...cur, bank: result.containers.bank, inventory: result.containers.inventory } });
    void get().saveNow();
    return true;
  },
  quickMove: (qty) => {
    const { selected, state } = get();
    if (!selected) return;
    const source = getSlot(containersOf(state), selected);
    if (!source) {
      set({ selected: null });
      return;
    }
    const targetContainer = selected.container === 'bank' ? 'inventory' : 'bank';
    const targetIndex = findQuickTarget(containersOf(state), targetContainer, source.itemId);
    if (targetIndex === null) {
      get().pushToast(
        targetContainer === 'bank' ? 'Bank is full.' : 'Inventory is full.',
        'warn',
      );
      return;
    }
    const moveQty = Math.min(qty ?? source.qty, source.qty);
    if (get().transferSlot(selected, { container: targetContainer, index: targetIndex }, moveQty)) {
      if (moveQty >= source.qty) set({ selected: null });
    }
  },
  equipFromSlot: (ref) => {
    const cur = get().state;
    const result = equipFrom({ containers: containersOf(cur), equipment: cur.equipment }, ref);
    if (!result.ok) {
      get().pushToast(EQUIP_FAILURE_TEXT[result.reason], 'warn');
      return;
    }
    set({
      state: {
        ...cur,
        bank: result.containers.bank,
        inventory: result.containers.inventory,
        equipment: result.equipment,
      },
      selected: null,
    });
    void get().saveNow();
  },
  unequipSlot: (slot) => {
    const cur = get().state;
    const result = unequip({ containers: containersOf(cur), equipment: cur.equipment }, slot);
    if (!result.ok) {
      get().pushToast(EQUIP_FAILURE_TEXT[result.reason], 'warn');
      return;
    }
    set({
      state: {
        ...cur,
        bank: result.containers.bank,
        inventory: result.containers.inventory,
        equipment: result.equipment,
      },
    });
    void get().saveNow();
  },
  acceptQuest: (questId) => {
    const cur = get().state;
    const q = QUESTS.quests.find((x) => x.id === questId);
    if (!q) return;
    // Duplicates are rejected in EVERY status: re-accepting a completed
    // quest would re-arm its exactly-once rewards. Gated quests (ship_it
    // before the Codex Quarry unlock) are refused with a visible reason.
    const check = canAcceptQuest(cur, questId);
    if (!check.ok) {
      if (cur.quests.every((qs) => qs.questId !== questId)) {
        get().pushToast(`${q.name}: ${check.reason}`, 'warn');
      }
      return;
    }
    const giverName = getNpc(q.giver).name;
    set({ state: { ...cur, quests: [...cur.quests, { questId, status: 'active', progress: 0 }] } });
    get().pushToast(`Quest accepted: ${q.name} (from ${giverName}).`, 'good');
    // Acceptance is a durable choice like every other mutating action.
    void get().saveNow();
  },
  speakToHarrow: (choice) => {
    const cur = get().state;
    const { state: next, resultKey } = applyHarrowChoice(cur, choice);
    if (next !== cur) {
      set({ state: next });
      void get().saveNow();
    }
    return resultKey;
  },
}));

/** Item display names. */
export const ITEM_NAMES: Record<ItemId, string> = Object.fromEntries(
  Object.entries(ITEMS.items).map(([k, v]) => [k, v.name]),
) as Record<ItemId, string>;
